import React, { useCallback, useContext, useState } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';

import AccountPickerContext from 'contexts/AccountPickerContext';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import useCarrierNameConfirmation from 'components/ConfirmationModals/hooks/useCarrierNameConfirmation';
import { Option } from 'kingpin/atoms/ContextMenu';
import STORE from 'store';

import ContextMenuContext from '../../../kingpin/atoms/ContextMenu/ContextMenuContext';
import useMappedUsers from '../../V3/Workspace/screens/UsersScreen/ProductAppUsersScreen/useMappedUsers';

const EntityDeleteOption = ({ entity }: { entity: EntityDetails.Entity }) => {
  const { closeMenu } = useContext(ContextMenuContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { mappedUsers, isLoading } = useMappedUsers({
    entityId: entity.id,
  });
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { getConfirmationThat, CarrierNameConfirmation } =
    useCarrierNameConfirmation({
      title: `Delete ${entity.name}`,
    });

  const onDeleteClicked = useCallback(async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    const isConfirmed = await getConfirmationThat(
      `You are about to delete the ${entity.name} and it's corresponding app`,
    );
    if (!isConfirmed) {
      closeMenu();
      return;
    }

    const entityApp = entityDetailsApps.find(
      (app) => app.entityId === entity.id,
    );
    if (entityApp) {
      await STORE.contentDistributions
        .getEntityDetailsApps({
          accountId: selectedAccountId,
        })
        .doc(entityApp.id)
        .delete();
    }

    await STORE.getEntityDefinitionsRef({ accountId: selectedAccountId })
      .doc(entity.id)
      .delete();

    setIsSaving(false);
    closeMenu();
  }, [
    closeMenu,
    entity.id,
    entity.name,
    entityDetailsApps,
    getConfirmationThat,
    isSaving,
    selectedAccountId,
  ]);

  if (isLoading) {
    return (
      <Option
        label={'Delete'}
        isDanger
        isDisabled
        disabledMessage={`Loading mapped users...`}
      />
    );
  }

  if (mappedUsers.length > 0) {
    return (
      <Option
        label={'Delete'}
        isDanger
        isDisabled
        disabledMessage={`You cannot delete this entity as the following users are mapped to it: ${mappedUsers.map((x) => `"${x.user.displayName}"`).join(', ')}`}
      />
    );
  }

  return (
    <>
      <Option
        label={'Delete'}
        isDanger
        onClick={onDeleteClicked}
        shouldHideInsteadOfCloseMenu
      />
      <ModalTransition>{CarrierNameConfirmation}</ModalTransition>
    </>
  );
};

export default EntityDeleteOption;
